import {
  ApplicationRef,
  DestroyRef,
  inject,
  Injectable,
} from '@angular/core';
import {Constants} from '../../../../../common/constants/constants';
import {OidcAuthService} from '../../../auth/oidc-auth/oidc-auth.service';
import {RedirectService} from '../../routing/redirect/redirect.service';
import {BrowserSessionStorageService} from '../browser-session-storage/browser-session-storage.service';
import {BackgroundTaskService} from '../../error/background-task/background-task.service';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {

  private authService: OidcAuthService = inject(OidcAuthService);


  private readonly backgroundTaskService: BackgroundTaskService = inject(BackgroundTaskService);

  private redirectService: RedirectService = inject(RedirectService);

  private browserSessionStorageService: BrowserSessionStorageService = inject(BrowserSessionStorageService);

  private appRef: ApplicationRef = inject(ApplicationRef);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    // Cross-tab path: a `storage` event fires only in OTHER tabs when another
    // tab writes the flag. The listener is bound to the injector's lifetime: the
    // app-level singleton attaches exactly one, but a TestBed teardown would
    // otherwise leave one behind per spec.
    const listenersController = new AbortController();
    this.destroyRef.onDestroy(() => listenersController.abort());
    window.addEventListener('storage', (event: StorageEvent): void => {
      if (event.key !== Constants.CLEAR_SESSION_STORAGE || !event.newValue) {
        return;
      }
      // The flag is `action:<uuid>` (the uuid only guarantees a value change so
      // the event always fires) — take the action before the separator.
      const action = event.newValue.split(Constants.COLON)[0];
      this.backgroundTaskService.runInBackground(this.runSessionAction(action));
    }, {signal: listenersController.signal});
  }

  /**
   * Method: Run the cross-tab session action and repaint the view it changed
   * @param action - One of Constants.SESSION_REFRESH or Constants.SESSION_LOGOUT
   */
  private async runSessionAction(action: string): Promise<void> {
    try {
      await this.handleSessionAction(action);
      this.appRef.tick();
    } catch {
      await this.redirectService.reloadPage();
    }
  }

  /**
   * Method: Clear the session and run the requested action in THIS tab. Called
   * both by the `storage` listener (cross-tab: another tab wrote the flag) and
   * directly by /clear-session (same tab). The flag is NOT removed here: the
   * value is unique per signal (`action:<uuid>`), so a leftover key never
   * suppresses a future event — and removing it would coalesce with the
   * same-tick broadcast in /clear-session and swallow the cross-tab signal.
   * The chunk-reload guard timestamp survives the clear, so the reload this
   * method may trigger stays guarded against a reload loop.
   * @param action - one of Constants.SESSION_REFRESH / SESSION_LOGOUT
   */
  public async handleSessionAction(action: string): Promise<void> {
    try {
      const chunkReloadTimestamp = this.browserSessionStorageService.getItem(Constants.CHUNK_RELOAD_TIMESTAMP);
      this.browserSessionStorageService.clearAll();
      if (chunkReloadTimestamp) {
        this.browserSessionStorageService.setItem(Constants.CHUNK_RELOAD_TIMESTAMP, chunkReloadTimestamp);
      }
      switch (action) {
        case Constants.SESSION_REFRESH:
          // Silently renew (signinSilent) to pull a fresh token + user in place
          // — this triggers the addUserLoaded handler that calls setToken/initLang.
          // login() (signinRedirect) is the wrong tool here: it does a full
          // redirect and is short-circuited by the isStartedLoginProcess guard,
          // so it often updates nothing. Fall back to a redirect only if the
          // silent renew fails (no active IdP session).
          try {
            await this.authService.renewToken();
          } catch {
            await this.authService.login();
          }
          break;
        case Constants.SESSION_LOGOUT:
          await this.authService.logout();
          break;
        default:
          await this.redirectService.reloadPage();
          break;
      }
    } catch {
      await this.redirectService.reloadPage();
    }
  }

}
